import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { taxPack, importYearReport, computeMonths, budgetModel, budgetVsActual, billList, forecast, unusualTransactions, recommendations, goalProjection, simulateDebts, monthlyEquivalent, catMapOf } from "./finance";
import { fmt, monthLabel, monthKey, round2, rateLabel } from "./format";

export function buildReport(state, month) {
  const months = computeMonths(state);
  const m = months[month] || computeMonths({ ...state, transactions: [] })[month] || { income: 0, expense: 0, savings: 0, net: 0, savingsRate: 0, byCat: {}, debt: 0 };
  const cats = catMapOf(state);
  const model = budgetModel(state);
  const bva = budgetVsActual(state, month, model);
  const { saved, suggestions } = billList(state);
  const bills = [...saved, ...suggestions.filter((s) => s.confidence !== "low")];
  const fc = forecast(state, 90);
  const byCat = Object.entries(m.byCat || {}).map(([id, v]) => ({ name: cats[id]?.name || id, group: cats[id]?.group || "", amount: round2(v) })).sort((a, b) => b.amount - a.amount);
  const debtSim = state.debts.length ? simulateDebts(state.debts, 0, "avalanche") : null;
  const summary = [
    `In ${monthLabel(month)} your household received ${fmt(m.income)} and spent ${fmt(m.expense)}${m.savings ? `, setting aside ${fmt(m.savings)} in savings` : ""}.`,
    m.net >= 0 ? `You finished the month with a surplus of ${fmt(m.net)}.` : `You finished the month with a deficit of ${fmt(-m.net)}.`,
    byCat[0] ? `Your largest spending category was ${byCat[0].name} (${fmt(byCat[0].amount)}).` : "",
    `Savings rate: ${m.savingsRate || 0}%.`,
  ].filter(Boolean).join(" ");
  return {
    month,
    title: `Household Financial Report — ${monthLabel(month)}`,
    summary,
    totals: m,
    byCat,
    bva,
    bills: bills.map((b) => ({ name: b.name, amount: b.amount, frequency: b.frequency, nextDate: b.nextDate, monthly: monthlyEquivalent(b.amount, b.frequency), status: b.id ? "Confirmed" : "Detected" })),
    debts: state.debts.map((d) => ({ ...d, payoff: debtSim?.perDebt.find((p) => p.id === d.id)?.payoffMonth || "—" })),
    debtPaid: m.debt,
    goals: state.goals.map((g) => ({ ...g, proj: goalProjection(g) })),
    forecast: fc,
    unusual: unusualTransactions(state, month),
    recs: recommendations(state),
    preliminary: model.preliminary,
    model,
  };
}

const money = (n) => fmt(n);

export function exportPDF(state, month) {
  const r = buildReport(state, month);
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  let y = 48;
  const h = (t) => {
    if (y > 680) {
      doc.addPage();
      y = 48;
    }
    doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(13, 148, 136).text(t, 40, y);
    doc.setTextColor(20, 20, 20);
    y += 8;
  };
  const table = (head, body) => {
    autoTable(doc, { startY: y, head: [head], body: body.length ? body : [head.map((_, i) => (i === 0 ? "No data" : ""))], margin: { left: 40, right: 40 }, styles: { fontSize: 9 }, headStyles: { fillColor: [13, 148, 136] } });
    y = doc.lastAutoTable.finalY + 22;
  };
  doc.setFont("helvetica", "bold").setFontSize(18).text(r.title, 40, y);
  y += 18;
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(110).text(`Generated ${new Date().toLocaleString()} · All figures from imported or user-entered data${r.preliminary ? " · Preliminary budget" : ""}`, 40, y);
  doc.setTextColor(20);
  y += 26;
  h("1. Executive summary");
  y += 8;
  doc.setFont("helvetica", "normal").setFontSize(10);
  const lines = doc.splitTextToSize(r.summary, W - 80);
  doc.text(lines, 40, y);
  y += lines.length * 13 + 14;
  h("2. Income and expenses");
  table(["Measure", "Amount"], [["Total income", money(r.totals.income)], ["Total expenses", money(r.totals.expense)], ["Savings contributions", money(r.totals.savings)], ["Net cash flow", money(r.totals.net)], ["Savings rate", `${r.totals.savingsRate || 0}%`]]);
  h("3. Spending by category");
  table(["Category", "Group", "Amount"], r.byCat.map((c) => [c.name, c.group, money(c.amount)]));
  h("4. Budget vs actual");
  table(["Category", "Budget", "Actual", "Remaining"], r.bva.map((b) => [b.name, money(b.available), money(b.actual), money(b.remaining)]));
  h("5. Recurring bills");
  table(["Bill", "Amount", "Frequency", "Next date", "Status"], r.bills.map((b) => [b.name, money(b.amount), b.frequency, b.nextDate, b.status]));
  h("6. Debt payments");
  table(["Creditor", "Balance", "Rate", "Min payment", "Est. payoff"], r.debts.map((d) => [d.creditor, money(d.balance), `${d.rate}%`, money(d.minPayment), d.payoff]));
  h("7. Savings progress");
  table(["Goal", "Target", "Saved", "Monthly", "Est. completion"], r.goals.map((g) => [g.name, money(g.target), money(g.current), money(g.monthly), g.proj.done ? "Complete" : g.proj.date || "—"]));
  h("8. Cash flow forecast (estimates)");
  table(["Window", "Expected income", "Upcoming bills", "Variable spending", "Projected balance"], r.forecast.windows.map((w) => [`${w.days} days`, money(w.income), money(w.bills), money(w.variable), money(w.endBalance)]));
  h("9. Unusual transactions");
  table(["Date", "Description", "Amount", "Why"], r.unusual.map((t) => [t.date, t.description, money(t.amount), t.reason]));
  h("10. Recommendations for next month");
  table(["Topic", "Recommendation"], r.recs.map((x) => [x.title, x.text]));
  doc.save(`household-report-${month}.pdf`);
}

export function exportOnePage(state, month) {
  const r = buildReport(state, month);
  const s = r.model.summary;
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  doc.setFont("helvetica", "bold").setFontSize(18).text("Household Budget Summary", 40, 50);
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(110).text(`${r.preliminary ? "PRELIMINARY · " : ""}Based on ${r.model.monthCount} month(s) of data · ${new Date().toLocaleDateString()}`, 40, 66);
  doc.setTextColor(20);
  autoTable(doc, {
    startY: 82,
    head: [["Monthly plan", "Amount"]],
    body: [["Income", money(s.income)], ["Expenses", money(s.expenses)], ["  Fixed", money(s.fixed)], ["  Variable", money(s.variable)], ["  Essential", money(s.essential)], ["  Discretionary", money(s.discretionary)], ["  Debt payments", money(s.debt)], ["Savings", money(s.savings)], ["Net cash flow", money(s.net)], ["Available discretionary income", money(s.availableDiscretionary)]],
    margin: { left: 40, right: 320 },
    styles: { fontSize: 9 },
    headStyles: { fillColor: [13, 148, 136] },
  });
  autoTable(doc, {
    startY: 82,
    head: [["Category", "Budget / mo"]],
    body: r.model.rows.filter((x) => x.type === "expense" && x.limit > 0).sort((a, b) => b.limit - a.limit).slice(0, 30).map((x) => [x.name, money(x.limit)]),
    margin: { left: 310, right: 40 },
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  doc.save(`budget-summary-${month}.pdf`);
}

function txRows(state, month) {
  const cats = catMapOf(state);
  const acc = Object.fromEntries(state.accounts.map((a) => [a.id, a.name]));
  return state.transactions
    .filter((t) => !month || monthKey(t.date) === month)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((t) => ({ Date: t.date, Description: t.description, Amount: t.amount, Category: cats[t.category]?.name || t.category, Account: acc[t.accountId] || "", Excluded: t.excluded ? "yes" : "", Business: t.business ? "yes" : "", Notes: t.notes || "" }));
}

export function exportXLSX(state, month) {
  const r = buildReport(state, month);
  const wb = XLSX.utils.book_new();
  const add = (name, rows) => XLSX.utils.book_append_sheet(wb, sheet(rows.length ? rows : [{ Note: "No data" }]), name);
  add("Summary", [{ Measure: "Income", Amount: r.totals.income }, { Measure: "Expenses", Amount: r.totals.expense }, { Measure: "Savings", Amount: r.totals.savings }, { Measure: "Net cash flow", Amount: r.totals.net }, { Measure: "Savings rate %", Amount: r.totals.savingsRate || 0 }, { Measure: "Summary", Amount: r.summary }]);
  add("By category", r.byCat.map((c) => ({ Category: c.name, Group: c.group, Amount: c.amount })));
  add("Budget vs actual", r.bva.map((b) => ({ Category: b.name, Budget: b.available, Actual: b.actual, Remaining: b.remaining })));
  add("Bills", r.bills.map((b) => ({ Bill: b.name, Amount: b.amount, Frequency: b.frequency, "Next date": b.nextDate, Status: b.status })));
  add("Debts", r.debts.map((d) => ({ Creditor: d.creditor, Balance: d.balance, "Rate %": d.rate, "Min payment": d.minPayment, "Est. payoff": d.payoff })));
  add("Goals", r.goals.map((g) => ({ Goal: g.name, Target: g.target, Saved: g.current, Monthly: g.monthly, Completion: g.proj.date || "" })));
  add("Forecast", r.forecast.windows.map((w) => ({ Days: w.days, Income: w.income, Bills: w.bills, Variable: w.variable, "Projected balance": w.endBalance })));
  add("Unusual", r.unusual.map((t) => ({ Date: t.date, Description: t.description, Amount: t.amount, Reason: t.reason })));
  add("Recommendations", r.recs.map((x) => ({ Topic: x.title, Recommendation: x.text })));
  add("Transactions", txRows(state, month));
  XLSX.writeFile(wb, `household-report-${month}.xlsx`);
}

export function exportCSV(state, month) {
  download(toCsv(txRows(state, month)), `transactions-${month || "all"}.csv`, "text/csv");
}

// Spreadsheet formula-injection guard: text from imported statements must never start a formula
const FORMULA = /^[=+\-@\t\r]/;
export const safeCell = (v) => (typeof v === "string" && FORMULA.test(v) ? `'${v}` : v);
const safeRows = (rows) => rows.map((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, safeCell(v)])));
const sheet = (rows) => XLSX.utils.json_to_sheet(safeRows(rows));
const toCsv = (rows) => Papa.unparse(safeRows(rows));

export function download(content, name, type = "application/json") {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

// Generates a realistic text-based sample bank statement PDF for testing the importer
export function sampleStatementPDF() {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const now = new Date();
  const y = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const Y = y.getFullYear();
  const M = y.getMonth() + 1;
  const last = new Date(Y, M, 0).getDate();
  const p = (n) => String(n).padStart(2, "0");
  const rows = [
    [2, "DIRECT DEP ACME CORP PAYROLL", 2875.4],
    [3, "ROCKET MORTGAGE MTG PMT", -1845.0],
    [5, "KROGER #1123 GROCERY", -142.37],
    [6, "STARBUCKS STORE 0921", -7.85],
    [8, "DUKE ENERGY ELECTRIC BILL", -156.22],
    [10, "COMCAST XFINITY INTERNET", -89.99],
    [11, "NETFLIX.COM", -15.49],
    [12, "SHELL OIL 57442", -48.1],
    [14, "OLIVE GARDEN 0441", -64.5],
    [15, "DIRECT DEP ACME CORP PAYROLL", 2875.4],
    [16, "ONLINE TRANSFER TO SAVINGS", -400.0],
    [18, "VERIZON WIRELESS BILL PAY", -142.5],
    [20, "TARGET T-1442 REFUND", 23.99],
    [22, "GEICO AUTO INS", -168.0],
    [24, "ATM WITHDRAWAL 1234 MAIN ST", -100.0],
    [25, "MONTHLY MAINTENANCE FEE", -12.0],
    [27, "NAVIENT STUDENT LOAN", -265.0],
  ];
  let bal = 3150.25;
  const opening = bal;
  doc.setFont("helvetica", "bold").setFontSize(16).text("Sample Community Bank", 40, 50);
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text("Everyday Checking Statement", 40, 68);
  doc.text(`Account Number: XXXXXX4821`, 40, 84);
  doc.text(`Statement Period: ${p(M)}/01/${Y} - ${p(M)}/${p(last)}/${Y}`, 40, 100);
  doc.text(`Beginning Balance $${opening.toLocaleString("en-US", { minimumFractionDigits: 2 })}`, 40, 124);
  const body = rows.map(([d, desc, amt]) => {
    bal = round2(bal + amt);
    const f = (n) => Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return [`${p(M)}/${p(d)}`, desc, amt > 0 ? f(amt) : "", amt < 0 ? f(amt) : "", f(bal)];
  });
  autoTable(doc, { startY: 140, head: [["Date", "Description", "Deposits", "Withdrawals", "Balance"]], body, styles: { fontSize: 9 }, theme: "plain" });
  const yEnd = doc.lastAutoTable.finalY + 20;
  doc.text(`Ending Balance $${bal.toLocaleString("en-US", { minimumFractionDigits: 2 })}`, 40, yEnd);
  doc.save(`sample-statement-${Y}-${p(M)}.pdf`);
}

export function exportImportReportPDF(state, year) {
  const r = importYearReport(state, year);
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  doc.setFont("helvetica", "bold").setFontSize(18).text(`Yearly Import Report — ${year}`, 40, 50);
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(110).text(`Generated ${new Date().toLocaleString()} · from imported data only`, 40, 66);
  doc.setTextColor(20);
  const t = (startY, head, body) => { autoTable(doc, { startY, head: [head], body: body.length ? body : [head.map((_, i) => (i ? "" : "None"))], margin: { left: 40, right: 40 }, styles: { fontSize: 9 }, headStyles: { fillColor: [13, 148, 136] } }); return doc.lastAutoTable.finalY + 18; };
  let y = t(80, ["Summary", "Value"], [["Statements", r.statements.length], ["Transactions", r.totals.transactions], ["Months covered", r.totals.months], ["Income", money(r.totals.income)], ["Spending", money(r.totals.expense)], ["Savings", money(r.totals.savings)], ["Net cash flow", money(r.totals.net)], ["Transfers matched", `${r.transfers.count} (${money(r.transfers.amount)})`], ["Duplicates skipped", r.totals.duplicatesSkipped]]);
  y = t(y, ["Needs review", "Count"], [["Flagged transactions", r.review.flagged.length], ["Unclassified transactions", r.review.unclassified.length], ["Statements not reconciled", r.review.unreconciled.length], ["Missing months", r.review.gapCount]]);
  y = t(y, ["Flagged transaction", "Date", "Amount", "Reason"], r.review.flagged.slice(0, 40).map((x) => [x.description, x.date, money(x.amount), x.flagReason || ""]));
  y = t(y, ["Statement", "Account", "Period", "Txns", "Reconciled"], r.statements.map((s) => [s.fileName || "", s.accountName, s.period?.end ? `${s.period.start || "?"} – ${s.period.end}` : "", s.count, s.reconciliation ? (s.reconciliation.ok ? "Yes" : "No") : "n/a"]));
  t(y, ["Transfer date", "From", "To", "Amount"], r.transfers.list.map((x) => [x.date, x.from || "", x.to || "", money(x.amount)]));
  doc.save(`import-report-${year}.pdf`);
}

const TAX_SECTIONS = [["business", "Business expenses"], ["charity", "Charitable donations"], ["medical", "Medical & healthcare"]];
const taxRows = (sec) => sec.rows.map((r) => ({ Date: r.date, Description: r.description, Category: r.category, Amount: r.amount, Account: r.account, Notes: r.notes, Receipts: r.receipts || 0 }));
const mileRows = (m) => m.rows.map((r) => ({ Date: r.date, Miles: Number(r.miles), Purpose: r.purpose || "", From: r.from || "", To: r.to || "", Deduction: Math.round(Number(r.miles) * m.rate * 100) / 100 }));

export function taxDoc(state, year, title = "Year-End Tax Pack") {
  const t = taxPack(state, year);
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  doc.setFont("helvetica", "bold").setFontSize(18).text(`${title} — ${year}`, 40, 50);
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(110).text("Summary of transactions you categorized or marked. Not tax advice — keep receipts and confirm with a tax professional.", 40, 66);
  doc.setTextColor(20);
  autoTable(doc, { startY: 80, head: [["Section", "Transactions", "Total"]], body: [...TAX_SECTIONS.map(([k, l]) => [l, t[k].rows.length, money(t[k].total)]), [`Business miles (${t.mileage.miles} mi × ${rateLabel(t.mileage.rate)}/mi)`, t.mileage.rows.length, money(t.mileage.deduction)]], margin: { left: 40, right: 40 }, styles: { fontSize: 10 }, headStyles: { fillColor: [13, 148, 136] } });
  let y = doc.lastAutoTable.finalY + 22;
  TAX_SECTIONS.forEach(([k, l]) => {
    if (y > 680) { doc.addPage(); y = 48; }
    doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(13, 148, 136).text(`${l} — ${money(t[k].total)}`, 40, y);
    doc.setTextColor(20);
    autoTable(doc, { startY: y + 8, head: [["Date", "Description", "Category", "Amount", "Notes", "Receipts"]], body: t[k].rows.length ? t[k].rows.map((r) => [r.date, r.description, r.category, money(r.amount), r.notes, r.receipts || ""]) : [["None", "", "", "", "", ""]], margin: { left: 40, right: 40 }, styles: { fontSize: 8 }, headStyles: { fillColor: [13, 148, 136] } });
    y = doc.lastAutoTable.finalY + 22;
  });
  if (y > 680) { doc.addPage(); y = 48; }
  doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(13, 148, 136).text(`Business miles — ${t.mileage.miles} mi · ${money(t.mileage.deduction)}`, 40, y);
  doc.setTextColor(20);
  autoTable(doc, { startY: y + 8, head: [["Date", "Miles", "Purpose", "From", "To", "Deduction"]], body: t.mileage.rows.length ? mileRows(t.mileage).map((r) => [r.Date, r.Miles, r.Purpose, r.From, r.To, money(r.Deduction)]) : [["None", "", "", "", "", ""]], margin: { left: 40, right: 40 }, styles: { fontSize: 8 }, headStyles: { fillColor: [13, 148, 136] } });
  return { doc, t };
}

export const exportTaxPDF = (state, year) => taxDoc(state, year).doc.save(`tax-pack-${year}.pdf`);

export function exportTaxXLSX(state, year) {
  const t = taxPack(state, year);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet(TAX_SECTIONS.map(([k, l]) => ({ Section: l, Transactions: t[k].rows.length, Total: t[k].total })).concat([{ Section: "Business miles", Transactions: t.mileage.rows.length, Total: t.mileage.deduction }])), "Summary");
  XLSX.utils.book_append_sheet(wb, sheet(t.mileage.rows.length ? mileRows(t.mileage) : [{ Note: "None" }]), "Business miles");
  TAX_SECTIONS.forEach(([k, l]) => XLSX.utils.book_append_sheet(wb, sheet(t[k].rows.length ? taxRows(t[k]) : [{ Note: "None" }]), l.slice(0, 31)));
  XLSX.writeFile(wb, `tax-pack-${year}.xlsx`);
}

export function exportTaxCSV(state, year) {
  const t = taxPack(state, year);
  download(toCsv(TAX_SECTIONS.flatMap(([k, l]) => taxRows(t[k]).map((r) => ({ Section: l, ...r }))).concat(mileRows(t.mileage).map((r) => ({ Section: "Business miles", Date: r.Date, Description: `${r.Miles} mi — ${r.Purpose}`, Category: "Mileage", Amount: r.Deduction, Account: "", Notes: [r.From, r.To].filter(Boolean).join(" → "), Receipts: "" })))), `tax-pack-${year}.csv`, "text/csv");
}

// Phones get the share sheet (email, Messages…); computers download. Returns "shared" | "saved" | null (cancelled)
export async function shareOrSave(blob, name, title, type = "application/pdf") {
  const file = new File([blob], name, { type });
  if (navigator.canShare?.({ files: [file] }) && window.matchMedia("(pointer: coarse)").matches) {
    try { await navigator.share({ files: [file], title }); return "shared"; } catch (e) { if (e.name === "AbortError") return null; }
  }
  download(blob, name, type);
  return "saved";
}
